"use client";

import { useEffect, useRef, useState } from "react";
import { Switch } from "@mui/material";
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag } from "lucide-react";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { useDispatch, useSelector } from "react-redux";
import ActionButtons from "./ActionButtons";
import { useNavigate, Link } from "react-router-dom";
import { setShowComments } from "../../store/ApplicationSlice";

export function ApplicationDetailsCard({
  isFullWidth,
  setIsFullWidth,
  selectedApplicationData
}) {
  const [isSticky, setIsSticky] = useState(false);
  const cardRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showComments = useSelector((state) => state.intakeData.showComments);

  useEffect(() => {
    const handleScroll = () => {
      if (cardRef.current) {
        const cardRect = cardRef.current.getBoundingClientRect();
        // Check if the card has reached the top with a small threshold
        setIsSticky(cardRect.top <= 5);
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const uniqueValues = useSelector((state) => state.intakeData.uniqueValues);
  const selectedApplicationId = useSelector(
    (state) => state.intakeData.selectedAppId
  );
  const applicationIds = uniqueValues?.application_id || [];

  const handleNextButtonClick = () => {
    const currentIndex = applicationIds.indexOf(selectedApplicationId);
    const nextApplicationId =
      currentIndex !== -1 && currentIndex < applicationIds.length - 1
        ? applicationIds[currentIndex + 1]
        : null;
    if (nextApplicationId) {
      window.location.href = `/app/intake/${nextApplicationId}`;
    }
  };

  const handleToggleChange = (event) => {
    dispatch(setShowComments(event.target.checked));
  };

  // Helper function to format date to MM/DD/YYYY
  const formatDateUS = (dateString) => {
    if (!dateString) return "---";
    const date = new Date(dateString);
    if (isNaN(date)) return "---";
    return date.toLocaleDateString("en-US");
  };

  return (
    <>
      {/* Fixed header that appears when scrolled - completely separate from main content */}
      {isSticky && (
        <div
          className="fixed top-0 left-0 right-0 bg-white p-4 shadow-md z-50"
          style={{ animation: "fadeIn 0.2s ease-in-out" }}
        >
          <div className="max-w-[1600px] mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="font-semibold ">
                #{selectedApplicationData.application_id || "---"}
              </span>
              <span className=" text-gray-600">
                {selectedApplicationData.customer_name || "---"}
              </span>
              <span
                className={`px-2 py-0.5 text-sm font-medium rounded-full ml-2 ${
                  selectedApplicationData.application_status === "under-review"
                    ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                    : selectedApplicationData.application_status === "reject"
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {selectedApplicationData.application_status || "Pending"}
              </span>
            </div>
            <ActionButtons />
          </div>
        </div>
      )}

      {/* Spacer div to prevent content jump when header becomes fixed */}
      {isSticky && <div className="h-16"></div>}

      {/* Main card content - always fully visible */}
      <div ref={cardRef} className="bg-white rounded-lg shadow-md mb-6 p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-bold">
                Application #{selectedApplicationData.application_id || "---"}
              </h1>
              <span
                className={`px-2 py-0.5 text-sm font-medium rounded-full ${
                  selectedApplicationData.application_status === "under-review"
                    ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                    : selectedApplicationData.application_status === "reject"
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {selectedApplicationData.application_status || "Pending"}
              </span>
            </div>
            <h2 className="text-lg text-gray-700">
              {selectedApplicationData.customer_name || "---"}
            </h2>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2 mt-2">
              {/* Toggle */}
              <div className="flex justify-end items-center">
                <div className="flex items-center gap-2 px-3 py-1 rounded border border-[#e5e7eb] bg-white shadow-sm">
                  <Switch
                    size="small"
                    checked={showComments}
                    onChange={handleToggleChange}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#1D4ED8" // Tailwind blue-700
                        },

                      "& .MuiSwitch-thumb": {
                        width: 16,
                        height: 16
                      }
                    }}
                  />
                  <span className="text-sm font-medium text-blue-700">
                    Notes ON/OFF
                  </span>
                </div>
              </div>
              <Link to="/app/details">
                <Button variant="outlined" size="sm" className="">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to List
                </Button>
              </Link>
              <Button
                variant="outlined"
                size="sm"
                className=""
                onClick={handleNextButtonClick}
              >
                <ArrowRight className="h-4 w-4 mr-1" />
                Next
              </Button>
            </div>
          </div>
        </div>
        <Separator className="my-3" />

        <div className="flex items-center justify-between w-full  text-gray-500">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>
                Submitted:{" "}
                {formatDateUS(selectedApplicationData.mail_received_at)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>
                Updated:{" "}
                {formatDateUS(
                  selectedApplicationData.application_last_modified
                )}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Tag className="h-4 w-4" />
              <span>Priority: {selectedApplicationData.priority || "---"}</span>
            </div>
          </div>
          <ActionButtons />
        </div>
      </div>
    </>
  );
}
